<template>
  <Teleport to="body">
    <transition name="fade">
      <div v-if="isOpen" class="palette-overlay" @click.self="close">
        <div class="palette-container">
          <div class="palette-input-wrapper">
            <svg class="palette-search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <circle cx="11" cy="11" r="8"/>
              <path d="M21 21l-4.35-4.35"/>
            </svg>
            <input
              ref="inputRef"
              v-model="query"
              type="text"
              class="palette-input"
              placeholder="搜索网站、工具，或输入 /ai 使用AI助手..."
              @keydown="handleKeydown"
            />
            <button class="palette-close" @click="close">
              <kbd>ESC</kbd>
            </button>
          </div>

          <div class="palette-content">
            <div v-if="query.startsWith('/ai ')" class="ai-mode">
              <div class="ai-prompt">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M12 2a4 4 0 014 4v1a3 3 0 013 3v1a3 3 0 01-1 2.24V16a4 4 0 01-4 4h-4a4 4 0 01-4-4v-2.76A3 3 0 015 11v-1a3 3 0 013-3V6a4 4 0 014-4z"/>
                </svg>
                <span>AI助手模式 - 按回车发送</span>
              </div>
            </div>

            <template v-else>
              <div v-if="recentSearches.length && !query" class="palette-section">
                <div class="section-header">
                  <span>最近搜索</span>
                  <button @click="clearHistory">清除</button>
                </div>
                <ul class="palette-list">
                  <li
                    v-for="(item, index) in recentSearches"
                    :key="item.id"
                    class="palette-item"
                    :class="{ active: selectedIndex === index }"
                    @click="selectRecent(item)"
                    @mouseenter="selectedIndex = index"
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <circle cx="12" cy="12" r="10"/>
                      <polyline points="12,6 12,12 16,14"/>
                    </svg>
                    <span>{{ item.keyword }}</span>
                  </li>
                </ul>
              </div>

              <div v-if="quickActions.length && !query" class="palette-section">
                <div class="section-header">快捷操作</div>
                <ul class="palette-list">
                  <li
                    v-for="(action, index) in quickActions"
                    :key="action.id"
                    class="palette-item"
                    :class="{ active: selectedIndex === recentSearches.length + index }"
                    @click="executeAction(action)"
                    @mouseenter="selectedIndex = recentSearches.length + index"
                  >
                    <component :is="action.icon" />
                    <span>{{ action.label }}</span>
                    <span class="item-shortcut" v-if="action.shortcut">
                      {{ action.shortcut }}
                    </span>
                  </li>
                </ul>
              </div>

              <div v-if="filteredBookmarks.length" class="palette-section">
                <div class="section-header">网站</div>
                <ul class="palette-list">
                  <li
                    v-for="(bookmark, index) in filteredBookmarks"
                    :key="bookmark.id"
                    class="palette-item"
                    :class="{ active: selectedIndex === getBookmarkIndex(index) }"
                    @click="openBookmark(bookmark)"
                    @mouseenter="selectedIndex = getBookmarkIndex(index)"
                  >
                    <img
                      v-if="bookmark.icon"
                      :src="bookmark.icon"
                      class="item-icon"
                      @error="e => e.target.style.display = 'none'"
                    />
                    <div class="item-icon-placeholder" v-else>
                      {{ bookmark.name.charAt(0) }}
                    </div>
                    <div class="item-info">
                      <span class="item-name">{{ bookmark.name }}</span>
                      <span class="item-url">{{ bookmark.url }}</span>
                    </div>
                  </li>
                </ul>
              </div>

              <div v-if="query && !filteredBookmarks.length" class="palette-empty">
                <p>没有找到匹配的结果</p>
                <button @click="searchWeb">使用搜索引擎搜索 "{{ query }}"</button>
              </div>
            </template>
          </div>
        </div>
      </div>
    </transition>
  </Teleport>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted, nextTick } from 'vue'
import { useNavStore } from '@/stores/nav'
import storage from '@/services/storage'

const navStore = useNavStore()
const isOpen = ref(false)
const query = ref('')
const selectedIndex = ref(0)
const inputRef = ref(null)
const recentSearches = ref([])

const quickActions = [
  {
    id: 'add-bookmark',
    label: '添加新书签',
    icon: { template: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>' },
    action: 'addBookmark'
  },
  {
    id: 'toggle-theme',
    label: '切换主题',
    icon: { template: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 12.79A9 9 0 1111.21 3 7 7 0 0021 12.79z"/></svg>' },
    action: 'toggleTheme',
    shortcut: '⌘T'
  },
  {
    id: 'ai-assistant',
    label: 'AI助手',
    icon: { template: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 2a4 4 0 014 4v1a3 3 0 013 3v1a3 3 0 01-1 2.24V16a4 4 0 01-4 4h-4a4 4 0 01-4-4v-2.76A3 3 0 015 11v-1a3 3 0 013-3V6a4 4 0 014-4z"/></svg>' },
    action: 'openAI',
    shortcut: '⌘I'
  },
  {
    id: 'settings',
    label: '打开设置',
    icon: { template: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-2 2 2 2 0 01-2-2v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83 0 2 2 0 010-2.83l.06-.06a1.65 1.65 0 00.33-1.82 1.65 1.65 0 00-1.51-1H3a2 2 0 01-2-2 2 2 0 012-2h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 010-2.83 2 2 0 012.83 0l.06.06a1.65 1.65 0 001.82.33H9a1.65 1.65 0 001-1.51V3a2 2 0 012-2 2 2 0 012 2v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 0 2 2 0 010 2.83l-.06.06a1.65 1.65 0 00-.33 1.82V9a1.65 1.65 0 001.51 1H21a2 2 0 012 2 2 2 0 01-2 2h-.09a1.65 1.65 0 00-1.51 1z"/></svg>' },
    action: 'settings',
    shortcut: '⌘,'
  }
]

const filteredBookmarks = computed(() => {
  if (!query.value || query.value.startsWith('/')) return []
  return navStore.bookmarks
    .filter(b => 
      b.name.toLowerCase().includes(query.value.toLowerCase()) ||
      b.url.toLowerCase().includes(query.value.toLowerCase()) ||
      (b.tags && b.tags.some(t => t.toLowerCase().includes(query.value.toLowerCase())))
    )
    .slice(0, 8)
})

function getBookmarkIndex(index) {
  const baseIndex = query.value ? 0 : recentSearches.value.length + quickActions.length
  return baseIndex + index
}

function open() {
  isOpen.value = true
  query.value = ''
  selectedIndex.value = 0
  loadRecentSearches()
  nextTick(() => {
    inputRef.value?.focus()
  })
}

function close() {
  isOpen.value = false
  query.value = ''
}

async function loadRecentSearches() {
  recentSearches.value = await storage.getSearchHistory(5)
}

async function clearHistory() {
  await storage.clearSearchHistory()
  recentSearches.value = []
}

function handleKeydown(e) {
  const totalItems = getTotalItems()
  
  switch (e.key) {
    case 'ArrowDown':
      e.preventDefault()
      selectedIndex.value = (selectedIndex.value + 1) % totalItems
      break
    case 'ArrowUp':
      e.preventDefault()
      selectedIndex.value = (selectedIndex.value - 1 + totalItems) % totalItems
      break
    case 'Enter':
      e.preventDefault()
      executeSelected()
      break
    case 'Escape':
      close()
      break
  }
}

function getTotalItems() {
  if (query.value && !query.value.startsWith('/')) {
    return filteredBookmarks.value.length
  }
  return recentSearches.value.length + quickActions.length
}

function executeSelected() {
  if (query.value.startsWith('/ai ')) {
    const aiQuery = query.value.slice(4)
    console.log('AI Query:', aiQuery)
    close()
    return
  }

  if (filteredBookmarks.value.length && query.value) {
    const bookmark = filteredBookmarks.value[selectedIndex.value]
    if (bookmark) {
      openBookmark(bookmark)
    }
    return
  }

  if (selectedIndex.value < recentSearches.value.length) {
    selectRecent(recentSearches.value[selectedIndex.value])
    return
  }

  const actionIndex = selectedIndex.value - recentSearches.value.length
  if (actionIndex < quickActions.length) {
    executeAction(quickActions[actionIndex])
  }
}

function selectRecent(item) {
  query.value = item.keyword
}

function executeAction(action) {
  switch (action.action) {
    case 'addBookmark':
      close()
      break
    case 'toggleTheme':
      navStore.toggleTheme()
      break
    case 'openAI':
      query.value = '/ai '
      break
    case 'settings':
      close()
      break
  }
}

function openBookmark(bookmark) {
  navStore.visitBookmark(bookmark.id)
  if (query.value) {
    storage.addSearchHistory(query.value)
  }
  window.open(bookmark.url, '_blank')
  close()
}

function searchWeb() {
  const searchUrl = `https://www.google.com/search?q=${encodeURIComponent(query.value)}`
  window.open(searchUrl, '_blank')
  storage.addSearchHistory(query.value)
  close()
}

function handleGlobalKeydown(e) {
  if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
    e.preventDefault()
    if (isOpen.value) {
      close()
    } else {
      open()
    }
  }
}

onMounted(() => {
  document.addEventListener('keydown', handleGlobalKeydown)
})

onUnmounted(() => {
  document.removeEventListener('keydown', handleGlobalKeydown)
})

defineExpose({ open, close })
</script>

<style lang="scss" scoped>
.palette-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(4px);
  z-index: 2000;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding-top: 15vh;
}

.palette-container {
  width: 100%;
  max-width: 640px;
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  border-radius: var(--radius-xl);
  box-shadow: var(--shadow-xl);
  overflow: hidden;
}

.palette-input-wrapper {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  padding: var(--spacing-md) var(--spacing-lg);
  border-bottom: 1px solid var(--border-color);
}

.palette-search-icon {
  width: 20px;
  height: 20px;
  color: var(--text-muted);
  flex-shrink: 0;
}

.palette-input {
  flex: 1;
  font-size: var(--text-lg);
  color: var(--text-primary);
  
  &::placeholder {
    color: var(--text-muted);
  }
}

.palette-close {
  kbd {
    padding: 4px 8px;
    background: var(--bg-tertiary);
    border: 1px solid var(--border-color);
    border-radius: var(--radius-sm);
    font-size: var(--text-xs);
    color: var(--text-muted);
  }
}

.palette-content {
  max-height: 400px;
  overflow-y: auto;
  
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: var(--border-color);
    border-radius: 3px;
  }
}

.palette-section {
  padding: var(--spacing-sm) 0;
}

.section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--spacing-xs) var(--spacing-lg);
  font-size: var(--text-xs);
  font-weight: 600;
  color: var(--text-muted);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  
  button {
    font-size: var(--text-xs);
    color: var(--primary);
    
    &:hover {
      text-decoration: underline;
    }
  }
}

.palette-list {
  padding: 0 var(--spacing-sm);
}

.palette-item {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  padding: var(--spacing-sm) var(--spacing-md);
  border-radius: var(--radius-md);
  cursor: pointer;
  transition: background var(--transition-fast);
  
  svg {
    width: 18px;
    height: 18px;
    color: var(--text-muted);
    flex-shrink: 0;
  }
  
  &:hover,
  &.active {
    background: var(--bg-tertiary);
  }
}

.item-icon {
  width: 24px;
  height: 24px;
  border-radius: var(--radius-sm);
  object-fit: contain;
}

.item-icon-placeholder {
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--primary);
  color: white;
  font-size: var(--text-sm);
  font-weight: 600;
  border-radius: var(--radius-sm);
}

.item-info {
  flex: 1;
  min-width: 0;
  
  .item-name {
    display: block;
    font-size: var(--text-sm);
    color: var(--text-primary);
  }
  
  .item-url {
    display: block;
    font-size: var(--text-xs);
    color: var(--text-muted);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

.item-shortcut {
  font-size: var(--text-xs);
  color: var(--text-muted);
  background: var(--bg-secondary);
  padding: 2px 6px;
  border-radius: var(--radius-sm);
}

.palette-empty {
  padding: var(--spacing-xl);
  text-align: center;
  
  p {
    color: var(--text-muted);
    margin-bottom: var(--spacing-md);
  }
  
  button {
    color: var(--primary);
    
    &:hover {
      text-decoration: underline;
    }
  }
}

.ai-mode {
  padding: var(--spacing-lg);
}

.ai-prompt {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  padding: var(--spacing-md);
  background: linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(139, 92, 246, 0.1) 100%);
  border: 1px solid rgba(59, 130, 246, 0.3);
  border-radius: var(--radius-md);
  color: var(--primary);
  
  svg {
    width: 20px;
    height: 20px;
  }
}
</style>
